/**
 * Public, unauthenticated routes.
 *
 * GPX has no existing precedent for this: `App.js` always mounts `<Main />`, which always
 * runs the login flow (`initialRouteName={token ? 'ScanPack' : 'SignIn'}` in
 * `src/components/main.js`) — there is no URL-based router and no prior public/marketing
 * page. This module is the first one, kept intentionally tiny: a path check `App.js` can
 * branch on BEFORE mounting the authenticated app at all, not a general-purpose router.
 *
 * Add future public paths here rather than duplicating the pattern in App.js.
 */

export const PUBLIC_COST_CALCULATOR_PATH = '/cost-calculator';

/**
 * The Fulfillment Error Cost Calculator's backend host when reached via the public route.
 *
 * `GeneralSetting.instance.cost_calculator_url` (see cost_calculators_controller.rb) is a
 * global singleton scoped to ONE specific GroovePacker tenant, not "the API" in general —
 * every GP install has its own `<tenant>.groovepackerapi.com` host. This is deliberately
 * the same tenant the legacy `admin.groovepacker.com/#/settings/cost_calculator` tool always
 * pointed at (that's the whole reason this port exists — that page requires an admin login
 * to reach). Verified live and public, 2026-09-08:
 *   curl https://admin.groovepackerapi.com/cost_calculator.json
 * returns the same defaults this app ships (`total_cost: 17`, etc.) with no auth header.
 *
 * A logged-in user reaching the calculator via Settings (not this public route) does NOT
 * use this constant — costCalculatorActions.js falls back to the normal AsyncStorage-based
 * per-tenant URL in that case.
 */
export const PUBLIC_COST_CALCULATOR_API_URL = 'https://admin.groovepackerapi.com';

/**
 * True when the current page URL is the public cost-calculator route. Trailing slashes and
 * case are normalized so `/cost-calculator`, `/cost-calculator/` and `/Cost-Calculator` all
 * match — a marketing redirect is exactly the kind of link that picks up a trailing slash.
 */
export function isPublicCostCalculatorRoute(pathname) {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  if (!path) return false;
  const normalized = path.toLowerCase().replace(/\/+$/, '') || '/';
  return normalized === PUBLIC_COST_CALCULATOR_PATH;
}
